import type {SerializablePlainData} from '../../../../history/domain/SerializablePlainData';
import {isMirrorAxisGeometryProvider} from '../../../../mesh/guards/isMirrorAxisGeometryProvider';
import {isSketchLineObject} from '../../../../mesh/guards/isSketchLineObject';
import type {ISketchObject} from '../../../../mesh/sketch/ISketchObject';
import {SketchEntityKind} from '../relation-compatibility/SketchEntityKinds';
import {SketchRelationKind} from '../relation-compatibility/SketchRelationKinds';
import {MultiTargetRelation} from './MultiTargetRelation';

let symmetricRelationCount = 0;

/**
 * 实体级双向对称关系定义。
 *
 * 本类只维护“镜像轴、源实体、镜像实体”的有序领域关系，不直接求解或写回几何。
 * 具体方程由统一参数编译器生成，运行时写回由参数求解协调器负责。
 */
export class SketchEntitySymmetricConstraint extends MultiTargetRelation<ISketchObject> {
    /** 关系类型。 */
    public relationType = SketchRelationKind.Symmetric;

    /**
     * 创建实体级对称关系。
     *
     * @param axisProvider 镜像轴提供者。
     * @param sourceEntity 源实体。
     * @param mirroredEntity 镜像实体。
     * @throws 仅提供部分目标，或完整目标不符合有序三元组协议时抛出错误。
     */
    public constructor(
        axisProvider?: ISketchObject,
        sourceEntity?: ISketchObject,
        mirroredEntity?: ISketchObject,
    ) {
        super();
        this.name = `${this.baseDisplayName}${symmetricRelationCount}`;
        symmetricRelationCount += 1;

        const suppliedTargetCount = Number(Boolean(axisProvider))
            + Number(Boolean(sourceEntity))
            + Number(Boolean(mirroredEntity));

        if (suppliedTargetCount !== 0 && suppliedTargetCount !== 3) {
            throw new Error('实体对称关系必须同时提供镜像轴、源实体和镜像实体。');
        }

        if (axisProvider && sourceEntity && mirroredEntity) {
            this.bindTargets([axisProvider, sourceEntity, mirroredEntity]);
        }
    }

    /** @returns 关系面板显示名。 */
    public override get baseDisplayName(): string {
        return '对称';
    }

    /**
     * 绑定并校验有序目标。
     *
     * 容器会对同一关系重复绑定各自 owner，因此已存在的同一对象按幂等处理；
     * 一旦三目标齐备，必须先校验完整元组，再提交到关系自身的目标集合。
     *
     * @param targets 待追加目标。
     * @throws 目标超过三个，或完整三元组协议不合法时抛出错误。
     */
    public override bindTargets(targets: Iterable<ISketchObject>): void {
        const currentTargets = this.getTargetsSnapshot();
        const nextTargets = [...currentTargets];

        for (const target of targets) {
            if (!nextTargets.includes(target)) {
                nextTargets.push(target);
            }
        }

        if (nextTargets.length > 3) {
            throw new Error('实体对称关系只能绑定镜像轴、源实体和镜像实体三个目标。');
        }

        if (nextTargets.length === 3) {
            this.assertValidTargetTuple(nextTargets as [ISketchObject, ISketchObject, ISketchObject]);
        }

        nextTargets.slice(currentTargets.length).forEach((target) => super.bindTarget(target));
    }

    /**
     * 绑定单个目标。
     *
     * @param target 待绑定目标。
     */
    public override bindTarget(target: ISketchObject): void {
        this.bindTargets([target]);
    }

    /**
     * 创建外部镜像轴的参数公式载荷。
     *
     * 草图直线轴已经作为 island 内参数实体参与求解，无需额外载荷；模型边、基准面
     * 或平面面则在每次快照采集时重新解析，并转换为目标草图局部二维固定边界。
     *
     * @returns 外部轴可解析时返回纯数据载荷；草图线轴或悬空引用返回 null。
     */
    public override createParametricFormulaPayload(): SerializablePlainData | null {
        const axisProvider = this.getTargetsSnapshot()[0];

        if (!axisProvider || isSketchLineObject(axisProvider) || !isMirrorAxisGeometryProvider(axisProvider)) {
            return null;
        }

        const geometry = axisProvider.resolveMirrorAxisGeometry();
        if (!geometry) {
            return null;
        }

        const startLocal = axisProvider.referencePlane.worldToLocal(geometry.startWorld);
        const endLocal = axisProvider.referencePlane.worldToLocal(geometry.endWorld);
        const values = [startLocal.x, startLocal.y, endLocal.x, endLocal.y];

        if (!values.every(Number.isFinite)) {
            return null;
        }

        return {
            kind: 'mirror-axis-line',
            referenceId: axisProvider.persistentId,
            start: [startLocal.x, startLocal.y],
            end: [endLocal.x, endLocal.y],
        };
    }

    /**
     * 校验关系三元组。
     *
     * @param targets 按“轴、源、镜像”排列的目标。
     * @throws 轴不可解析、目标重复或两侧实体族不一致时抛出错误。
     */
    private assertValidTargetTuple(
        targets: readonly [ISketchObject, ISketchObject, ISketchObject],
    ): void {
        const [axisProvider, sourceEntity, mirroredEntity] = targets;
        const sourceKind = this.normalizeMirrorEntityKind(sourceEntity.objectType);
        const mirroredKind = this.normalizeMirrorEntityKind(mirroredEntity.objectType);

        if (new Set(targets).size !== 3
            || !isMirrorAxisGeometryProvider(axisProvider)
            || sourceKind === null
            || sourceKind !== mirroredKind) {
            throw new Error('实体对称关系目标非法：必须按“合法镜像轴、同类型源实体、同类型镜像实体”排序且互不相同。');
        }
    }

    /**
     * 把运行时对象类型归一为镜像公式支持的实体族。
     *
     * @param objectType 草图对象类型。
     * @returns 支持的实体族；Collection 与外部引用等非镜像实体返回 null。
     */
    private normalizeMirrorEntityKind(objectType: string): string | null {
        if (objectType === SketchEntityKind.EndPoint) {
            return SketchEntityKind.Point;
        }
        if (objectType === SketchEntityKind.CenterLine) {
            return SketchEntityKind.Line;
        }

        const supportedKinds: readonly string[] = [
            SketchEntityKind.Point,
            SketchEntityKind.Line,
            SketchEntityKind.Circle,
            SketchEntityKind.Arc,
            SketchEntityKind.Ellipse,
            SketchEntityKind.Parabola,
            SketchEntityKind.Spline,
            SketchEntityKind.Slot,
        ];

        return supportedKinds.includes(objectType) ? objectType : null;
    }
}
